/**
 * „Pomoc i informacje" — the card beside the Aplikacja tab: the trainer welcome, three published
 * pages and the build.
 *
 * The three addresses are `shared/config/legalLinks`, copied verbatim from the mobile app's own
 * address book. They are literals rather than configuration because there is exactly one set of
 * documents across every environment, and the `.html` suffix is load-bearing: the marketing site is
 * static with no extensionless routing, so `/kontakt` answers 404. Do not retype one here.
 *
 * ── Each link says that it leaves ───────────────────────────────────────────────────────────
 * `target="_blank"` with `rel="noopener noreferrer"`, and the accessible name CONTAINS the visible
 * label plus the fact — „Regulamin — otwiera się w nowej karcie" — because a name that does not
 * contain its own visible words fails SC 2.5.3, and a new tab opening unannounced is the thing SC
 * 3.2.5 is about.
 *
 * ── The version is inert ────────────────────────────────────────────────────────────────────
 * A row, not a link and not a button. It reads `__APP_VERSION__`, which `vite.config.ts` defines
 * from `package.json` — the least invasive way to expose it, since nothing else in this app needed
 * the number and a `VITE_*` variable would be configuration with nothing to configure.
 */

import { useTranslation } from 'react-i18next';
import { Link } from '@tanstack/react-router';

import { LEGAL_LINKS } from '~/shared/config/legalLinks';
import { ChevronRightIcon } from '~/shared/ui/AuthIcons';

import { ExternalIcon } from './AccountIcons';
import screen from './AccountScreen.module.css';
import styles from './HelpCard.module.css';

export function HelpCard() {
  const { t } = useTranslation();

  return (
    <div className={screen.sidePanel}>
      <div className={screen.card}>
        <h2 className={screen.cardTitle}>{t('web:account.app.help.title')}</h2>

        {/* The trainer welcome, any time — the same film a new trainer sees after registering.
            Preloaded with the card: the route's chunk and the logo's tone are ready before the press. */}
        <Link to="/powitanie" preload="render" className={`${String(screen.row)} ${String(styles.link)}`}>
          <span className={screen.rowText}>
            <b className={screen.rowTitle}>{t('web:welcome.account.open')}</b>
          </span>
          {/* A caret, where its siblings draw the arrow leaving the app: this one stays inside it. */}
          <ChevronRightIcon className={screen.rowIcon} />
        </Link>

        {LEGAL_LINKS.map((link) => {
          const label = t(link.labelKey);
          return (
            <a
              key={link.id}
              className={`${String(screen.row)} ${String(styles.link)}`}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={t('web:account.app.help.opensExternally', { label })}
            >
              <span className={screen.rowText}>
                <b className={screen.rowTitle}>{label}</b>
              </span>
              <ExternalIcon className={screen.rowIcon} />
            </a>
          );
        })}

        <div className={screen.row}>
          <span className={screen.rowText}>
            <b className={screen.rowTitle}>{t('web:account.app.help.version')}</b>
          </span>
          <span className={screen.rowValue}>{__APP_VERSION__}</span>
        </div>
      </div>
    </div>
  );
}
